import { faEnvelope, faLocationDot, faPhone } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import constants from "../constants/constants";
import { useEffect, useState } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";
import { useUserParams } from "../hooks/useUserParams";

export default function Contact() {
  const [popup, setPopup] = useState(false);
  const {
    getCurrentAgentName,
    getCurrentAgentTitle,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentBrokerage,
  } = useUserParams();
  const [formData, setFormData] = useState({
    Name: "",
    "Email Address": "",
    Message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  async function submitForm(event) {
    event.preventDefault();

    const data = {
      ...formData,
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Contact Form Message",
    };

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(data),
      });

      const response = await res.json();

      if (response?.success) {
        console.log("Success", response);
        setPopup({
          subject: "Thanks for your message!",
          message: "We will get back to you as soon as possible",
        });
        // Clear form after successful submission
        setFormData({
          Name: "",
          "Email Address": "",
          Message: "",
        });
      } else {
        setPopup({
          subject: "We encountered an error in sending this message",
          message: "Please try again later",
        });
      }
    } catch (err) {
      console.log(err);
      setPopup({
        subject: "We encountered an error in sending this message",
        message: "Please try again later",
      });
    }
  }

  const seoConfig = pageSEO.contact();
  return (
    <>
      <SEO {...seoConfig} />

      <section className="relative py-32 bg-[#fffdfa] overflow-hidden">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-16"
        >
          {/* Heading */}
          <div className="mb-14">
            <motion.div variants={fadeUp}>
              <div className="flex items-center gap-5 mb-8">
                <div className="h-px w-16 bg-[#8b8172]" />

                <span className="text-xs uppercase tracking-[0.45em] text-[#6d655b]">
                  Contact
                </span>
              </div>
            </motion.div>

            <motion.h2
              variants={fadeUp}
              className="font-serif text-5xl lg:text-6xl leading-[1.05] text-[#2b2925]"
            >
              Let's Start A Conversation
            </motion.h2>

            <motion.p
              variants={fadeUp}
              className="mt-8 max-w-2xl text-lg leading-9 text-[#5f594f]"
            >
              Whether you're buying your first home, selling a property, or simply
              looking for trusted advice, I'd be delighted to help you navigate your
              real estate journey.
            </motion.p>
          </div>

          <motion.div
            variants={fadeUp}
            className="grid gap-10 lg:grid-cols-[1.2fr_.8fr]"
          >
            {/* FORM */}
            <div className="border border-[#d8d0c6] bg-white p-10 lg:p-14">
              <div className="mb-12">
                <h3 className="font-serif text-4xl text-[#2b2925]">
                  Send A Message
                </h3>

                <p className="mt-4 text-[#5f594f] leading-8">
                  Complete the form below and we'll respond as soon as possible.
                </p>
              </div>

              <form onSubmit={submitForm} className="space-y-10">
                <div>
                  <label className="block text-xs uppercase tracking-[0.35em] text-[#6d655b] mb-4">
                    Full Name
                  </label>

                  <input
                    name="Name"
                    value={formData.Name}
                    onChange={handleInputChange}
                    placeholder="John Smith"
                    className="w-full border-b border-[#cfc7bc] bg-transparent py-4 text-lg text-[#2b2925] placeholder:text-[#8b8172] outline-none transition focus:border-[#8b8172]"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.35em] text-[#6d655b] mb-4">
                    Email Address
                  </label>

                  <input
                    name="Email Address"
                    value={formData["Email Address"]}
                    onChange={handleInputChange}
                    placeholder="john@email.com"
                    className="w-full border-b border-[#cfc7bc] bg-transparent py-4 text-lg text-[#2b2925] placeholder:text-[#8b8172] outline-none transition focus:border-[#8b8172]"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.35em] text-[#6d655b] mb-4">
                    Message
                  </label>

                  <textarea
                    rows={6}
                    name="Message"
                    value={formData.Message}
                    onChange={handleInputChange}
                    placeholder="Tell us how we can help..."
                    className="w-full resize-none border-b border-[#cfc7bc] bg-transparent py-4 text-lg text-[#2b2925] placeholder:text-[#8b8172] outline-none transition focus:border-[#8b8172]"
                  />
                </div>

                <button
                  type="submit"
                  className="inline-flex border border-[#8b8172] px-10 py-4 text-xs uppercase tracking-[0.35em] text-[#2b2925] transition-all duration-300 hover:bg-[#2b2925] hover:text-[#eee9df]"
                >
                  Send Message
                </button>
              </form>
            </div>

            {/* CONTACT DETAILS */}

            <div>
              <span className="text-xs uppercase tracking-[0.45em] text-[#6d655b]">
                Contact Details
              </span>

              <h3 className="mt-6 font-serif text-5xl text-[#2b2925]">
                {getCurrentAgentName()}
              </h3>

              <p className="mt-5 text-lg leading-8 text-[#5f594f]">
                {getCurrentAgentTitle()}
                <br />
                {getCurrentBrokerage()}
              </p>

              <div className="mt-8 space-y-5">
                {/* Address */}
                <div className="border-[15px] border-[#d3d2d0]/70 p-5">
                  <div className="flex items-start gap-5">
                    <FontAwesomeIcon
                      icon={faLocationDot}
                      className="mt-1 text-xl text-[#8b8172]"
                    />

                    <div>
                      <h4 className="text-xs uppercase tracking-[0.35em] text-[#6d655b]">
                        Office
                      </h4>

                      <p className="mt-4 leading-8 text-[#5f594f]">
                        {constants.AGENT_ADDRESS}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Phone */}
                <div className="border-[15px] border-[#d3d2d0]/70 p-5">
                  <div className="flex items-start gap-5">
                    <FontAwesomeIcon
                      icon={faPhone}
                      className="mt-1 text-xl text-[#8b8172]"
                    />

                    <div>
                      <h4 className="text-xs uppercase tracking-[0.35em] text-[#6d655b]">
                        Phone
                      </h4>

                      <p className="mt-4 text-lg text-[#2b2925]">
                        {getCurrentAgentNumber()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="border-[15px] border-[#d3d2d0]/70 p-5">
                  <div className="flex items-start gap-5">
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="mt-1 text-xl text-[#8b8172]"
                    />

                    <div>
                      <h4 className="text-xs uppercase tracking-[0.35em] text-[#6d655b]">
                        Email
                      </h4>

                      <p className="mt-4 text-lg text-[#2b2925]">
                        {getCurrentAgentEmail()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 border-t border-[#d8d0c6] pt-8">
                <p className="leading-8 text-[#5f594f]">
                  Every client deserves thoughtful guidance, honest communication,
                  and a strategy tailored to their unique real estate goals. I look
                  forward to helping you make your next move with confidence.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {popup && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />

          <div className="fixed left-1/2 top-1/2 z-50 w-[90%] max-w-lg -translate-x-1/2 -translate-y-1/2 border border-[#d8d0c6] bg-[#fffdfa] p-12 text-center shadow-2xl">
            <div className="mx-auto mb-8 flex h-16 w-16 items-center justify-center rounded-full border border-[#8b8172] text-2xl text-[#8b8172]">
              ✓
            </div>

            <h3 className="font-serif text-4xl text-[#2b2925]">
              {popup.subject}
            </h3>

            <p className="mt-6 leading-8 text-[#5f594f]">
              {popup.message}
            </p>

            <button
              onClick={() => setPopup(false)}
              className="mt-10 inline-flex border border-[#8b8172] px-10 py-4 text-xs uppercase tracking-[0.35em] text-[#2b2925] transition-all duration-300 hover:bg-[#2b2925] hover:text-[#eee9df]"
            >
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}
